import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Dumbbell, Beaker, Brain, Zap, Clock, ArrowRight, Filter, Sparkles, Shield, Footprints, Sun, FlaskConical, Wheat } from 'lucide-react';

const INTERACTIVE_GUIDES = [
  {
    slug: 'the-ammortal-chamber-a-deep-dive-into-the-future-of-wellness',
    title: 'The Ammortal Chamber',
    subtitle: '10 Recovery Therapies in 30 Minutes',
    category: 'Biohacking',
    readTime: 14,
    icon: Zap,
    color: '#f5a623',
    features: ['Modality Comparison Tool', 'Location Finder', 'Lead Capture'],
    description: 'A $159,500 multi-modality wellness pod that stacks PEMF, red light, molecular hydrogen, and 7 more modalities into one synchronized session.',
    tags: ['PEMF', 'Red Light', 'Hydrogen', 'Recovery'],
  },
  {
    slug: 'the-wolverine-complex-the-ultimate-peptide-stack-for-rapid-recovery-and-healing',
    title: 'The Wolverine Complex',
    subtitle: 'BPC-157 + TB-500 Stack Guide',
    category: 'Peptides',
    readTime: 12,
    icon: Shield,
    color: '#22c55e',
    features: ['Peptide Deep-Dives', 'Protocol Table', 'Stacking Options'],
    description: 'The ultimate peptide stack for rapid recovery. How BPC-157 and TB-500 work synergistically to accelerate tissue repair and reduce inflammation.',
    tags: ['BPC-157', 'TB-500', 'Recovery', 'Healing'],
  },
  {
    slug: 'the-ultimate-guide-to-hyrox-training-plans-and-workouts-get-race-ready',
    title: 'HYROX Training Guide',
    subtitle: '8 Weeks to Race-Ready',
    category: 'HYROX',
    readTime: 15,
    icon: Footprints,
    color: '#c41e3a',
    features: ['8-Station Breakdown', 'Weekly Training Plan', 'Pacing Strategy'],
    description: 'Complete 8-week training program with station breakdowns, benchmark targets, pacing strategy, and race-day nutrition protocol.',
    tags: ['Training', 'Race Prep', 'Stations', 'Pacing'],
  },
  {
    slug: 'peptide-side-effects-what-the-research-actually-says',
    title: 'Peptide Side Effects',
    subtitle: 'What the Research Actually Says',
    category: 'Peptides',
    readTime: 18,
    icon: Beaker,
    color: '#8b5cf6',
    features: ['Compare Peptides Tool', 'Decision Helper Quiz', 'Collapsible Sections'],
    description: 'Interactive guide to peptide side effects with a comparison tool and decision helper. Real research, not Reddit posts.',
    tags: ['Side Effects', 'Safety', 'Research', 'Compare'],
  },
  {
    slug: 'retatrutide-cancer-prevention-science',
    title: 'Retatrutide & Cancer Prevention',
    subtitle: 'Triple-Agonist Tumor Reduction Science',
    category: 'Peptides',
    readTime: 22,
    icon: FlaskConical,
    color: '#E63946',
    features: ['Tumor Data Visualization', 'Drug Comparison Table', 'Liver Stats Dashboard'],
    description: '14-fold tumor volume reduction vs. semaglutide. The science behind retatrutide\'s triple-agonist mechanism for cancer prevention.',
    tags: ['Retatrutide', 'Cancer', 'GLP-1', 'Research'],
  },
  {
    slug: 'how-to-train-like-an-athlete',
    title: 'Train Like an Athlete',
    subtitle: 'Complete Athletic Training Guide',
    category: 'Fitness',
    readTime: 10,
    icon: Dumbbell,
    color: '#3b82f6',
    features: ['Interactive Weekly Plan', 'Macro Breakdown', 'Daily Routine Timeline'],
    description: 'Structured training, periodization, nutrition, and daily habits that separate athletes from casual gym-goers.',
    tags: ['Training', 'Nutrition', 'Periodization', 'Recovery'],
  },
  {
    slug: 'hyrox-vs-crossfit-comparison',
    title: 'HYROX vs CrossFit',
    subtitle: 'Interactive Comparison Guide',
    category: 'HYROX',
    readTime: 11,
    icon: Footprints,
    color: '#c41e3a',
    features: ['Head-to-Head Comparison', 'Cost Breakdown', '5-Question Quiz'],
    description: 'Format face-off, cost analysis, and a quiz to find whether HYROX or CrossFit is the right fit for you.',
    tags: ['Comparison', 'CrossFit', 'Cost', 'Quiz'],
  },
  {
    slug: 'high-protein-meal-prep-guide',
    title: 'High Protein Meal Prep',
    subtitle: '4 Recipes, 1 Grocery List, 126g+ Daily',
    category: 'Nutrition',
    readTime: 8,
    icon: Sun,
    color: '#22c55e',
    features: ['Interactive Recipes', 'Weekly Planner', 'Grocery Checklist'],
    description: '4 high-protein recipes, a weekly meal plan, and an interactive grocery checklist. 2-3 hours of prep for the entire week.',
    tags: ['Meal Prep', 'Protein', 'Recipes', 'Nutrition'],
  },
  {
    slug: 'new-food-pyramid-2026-dietary-guidelines',
    title: 'The New Food Pyramid 2026',
    subtitle: 'RFK\'s Real Food Revolution',
    category: 'Nutrition',
    readTime: 16,
    icon: Wheat,
    color: '#f59e0b',
    features: ['Inverted Pyramid Visual', 'Old vs New Comparison', 'Build Your Plate', '5-Question Quiz'],
    description: 'The 2025-2030 Dietary Guidelines explained: more protein, full-fat dairy, less processed food, and an inverted pyramid that changes everything.',
    tags: ['Food Pyramid', 'USDA', 'Dietary Guidelines', 'Nutrition Policy'],
  },
];

const CATEGORIES = ['All', 'Peptides', 'HYROX', 'Biohacking', 'Fitness', 'Nutrition'];

const PROGRESS_KEY = 'ninjathlete_reading_progress';

function getProgress() {
  try {
    return JSON.parse(localStorage.getItem(PROGRESS_KEY) || '{}');
  } catch { return {}; }
}

function setProgressForSlug(slug, pct) {
  const p = getProgress();
  p[slug] = Math.max(p[slug] || 0, pct);
  localStorage.setItem(PROGRESS_KEY, JSON.stringify(p));
}

function GuideCard({ guide, progress }) {
  const Icon = guide.icon;
  const pct = progress[guide.slug] || 0;
  const isStarted = pct > 0;
  const isComplete = pct >= 95;

  return (
    <Link
      to={`/blogs/article/${guide.slug}`}
      className="group block border border-zinc-800 hover:border-zinc-700 transition-all overflow-hidden"
      data-testid={`guide-card-${guide.slug.slice(0, 30)}`}
    >
      {/* Progress bar */}
      <div className="h-1 bg-zinc-900">
        <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: guide.color }} />
      </div>

      <div className="p-5 sm:p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${guide.color}15`, border: `1px solid ${guide.color}30` }}>
            <Icon className="w-5 h-5" style={{ color: guide.color }} />
          </div>
          <div className="flex items-center gap-2">
            {isComplete && (
              <span className="text-[9px] font-mono uppercase tracking-wider text-green-400 bg-green-950/40 border border-green-900/30 px-2 py-0.5" data-testid="badge-complete">Complete</span>
            )}
            {isStarted && !isComplete && (
              <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-0.5" style={{ color: guide.color, backgroundColor: `${guide.color}10`, border: `1px solid ${guide.color}20` }} data-testid="badge-in-progress">{Math.round(pct)}%</span>
            )}
            <span className="text-[9px] font-mono text-zinc-600 border border-zinc-800 px-2 py-0.5">{guide.category}</span>
          </div>
        </div>

        {/* Title */}
        <h3 className="font-heading text-xl sm:text-2xl text-white group-hover:text-[#c41e3a] transition-colors leading-tight mb-1" data-testid="guide-card-title">
          {guide.title}
        </h3>
        <p className="text-xs font-mono text-zinc-500 mb-3">{guide.subtitle}</p>

        {/* Description */}
        <p className="text-sm text-zinc-400 leading-relaxed mb-4">{guide.description}</p>

        {/* Features */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {guide.features.map((f, i) => (
            <span key={f} className="text-[10px] font-mono text-zinc-500 bg-zinc-900 border border-zinc-800 px-2 py-1">{f}</span>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-zinc-500">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{guide.readTime} min</span>
            <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" />Interactive</span>
          </div>
          <span className="text-xs font-mono text-[#c41e3a] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
            {isStarted ? 'Continue' : 'Read'} <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function GuidesHubPage() {
  const [filter, setFilter] = useState('All');
  const [progress, setProgress] = useState({});

  useEffect(() => {
    setProgress(getProgress());

    // Listen for progress updates from article pages
    const handleStorage = (e) => {
      if (e.key === PROGRESS_KEY) setProgress(getProgress());
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const filtered = filter === 'All'
    ? INTERACTIVE_GUIDES
    : INTERACTIVE_GUIDES.filter(g => g.category === filter);

  const totalGuides = INTERACTIVE_GUIDES.length;
  const startedGuides = INTERACTIVE_GUIDES.filter(g => (progress[g.slug] || 0) > 0).length;
  const completedGuides = INTERACTIVE_GUIDES.filter(g => (progress[g.slug] || 0) >= 95).length;
  const avgProgress = INTERACTIVE_GUIDES.reduce((sum, g) => sum + (progress[g.slug] || 0), 0) / totalGuides;

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="guides-hub-page">
      {/* Hero */}
      <section className="relative pt-28 pb-12 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(196,30,58,0.05)_0%,transparent_60%)]" />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="w-5 h-5 text-[#c41e3a]" />
            <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-[#c41e3a] border border-[#c41e3a]/30 px-3 py-1">Interactive Guides</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-white mb-4" data-testid="hub-title">
            DEEP DIVE<br/><span className="text-[#c41e3a]">RESEARCH GUIDES</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 max-w-xl leading-relaxed">
            In-depth, interactive articles with collapsible sections, comparison tools, calculators, and data tables. Not your average blog posts.
          </p>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-6 pb-20">
        {/* Progress overview */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10" data-testid="progress-overview">
          {[
            { label: 'Guides', value: totalGuides, sub: 'Interactive articles' },
            { label: 'Started', value: startedGuides, sub: `of ${totalGuides}` },
            { label: 'Completed', value: completedGuides, sub: `of ${totalGuides}` },
            { label: 'Avg Progress', value: `${Math.round(avgProgress)}%`, sub: 'Across all guides' },
          ].map((s, i) => (
            <div key={s.label} className="border border-zinc-800 p-4" data-testid={`progress-stat-${i}`}>
              <p className="text-2xl font-heading text-white leading-none mb-1">{s.value}</p>
              <p className="text-[10px] font-mono tracking-wider uppercase text-[#c41e3a]">{s.label}</p>
              <p className="text-[10px] text-zinc-600">{s.sub}</p>
            </div>
          ))}
        </div>

        {/* Category filter */}
        <div className="flex items-center gap-3 mb-8">
          <Filter className="w-3.5 h-3.5 text-zinc-600" />
          <div className="flex gap-2">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider border transition-colors cursor-pointer ${
                  filter === cat
                    ? 'border-[#c41e3a] text-[#c41e3a] bg-[#c41e3a]/[0.05]'
                    : 'border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700'
                }`}
                data-testid={`filter-${cat.toLowerCase()}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Guide cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="guides-grid">
          {filtered.map(guide => (
            <GuideCard key={guide.slug} guide={guide} progress={progress} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <p className="text-sm text-zinc-500">No guides in this category yet.</p>
          </div>
        )}

        {/* More coming */}
        <div className="mt-12 border border-zinc-800 border-dashed p-8 text-center">
          <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-600 mb-2">Coming Soon</p>
          <p className="text-sm text-zinc-400">More interactive guides being built: Red Light Therapy, The Anterior Midcingulate Cortex, and more.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 mt-4 text-xs font-mono text-[#c41e3a] hover:underline">
            Browse all articles <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </main>
    </div>
  );
}

// Export progress tracking utility for use in article components
export { PROGRESS_KEY, setProgressForSlug, getProgress };
